import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import GifPicker from './GifPicker';
import { CameraModal } from './LazyModals';
import MentionSuggestions from './MentionSuggestions';
import { compressMediaFiles } from '../utils/imageCompression';
import { getMediaUrl } from '../utils/mediaUrl';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';

const CreateComment = ({
  postId,
  parentCommentId,
  replyToUser,
  onCommentCreated,
  onCancel,
  compact = false,
  placeholder,
}) => {
  const { user, refreshUser } = useAuth();
  const toast = useToast();
  const userProfilePic = user?.profilePicture || user?.profile_picture;

  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [media, setMedia] = useState([]);
  const [showGifPicker, setShowGifPicker] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [locationLoading, setLocationLoading] = useState(false);
  const [avatarError, setAvatarError] = useState(false);

  useEffect(() => {
    if (user && !user.profilePicture && !user.profile_picture) {
      refreshUser?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  useEffect(() => {
    setAvatarError(false);
  }, [userProfilePic]);
  const fileInputRef = useRef(null);
  const textFileInputRef = useRef(null);
  const textareaRef = useRef(null);

  // Auto-resize textarea height
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 240)}px`;
    }
  }, [content]);

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    if (media.length + files.length > 4) {
      toast.error('Maximum 4 attachments allowed');
      return;
    }

    const validFiles = [];
    const validTypes = [
      'image/jpeg',
      'image/jpg',
      'image/png',
      'image/gif',
      'image/webp',
      'video/mp4',
      'video/webm',
      'video/quicktime',
    ];

    for (const file of files) {
      if (!validTypes.includes(file.type)) {
        toast.error(`${file.name} is not a supported file type.`);
        continue;
      }

      if (file.size > 50 * 1024 * 1024) {
        toast.error(`${file.name} is too large (max 50MB).`);
        continue;
      }

      validFiles.push(file);
    }

    if (validFiles.length === 0) return;

    setUploading(true);
    try {
      const filesToUpload = await compressMediaFiles(validFiles, { type: 'post' });
      const formData = new FormData();
      formData.append('subfolder', 'posts');
      filesToUpload.forEach((file) => {
        formData.append('files', file);
      });

      const response = await axios.post('/api/upload/multiple', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      setMedia((prev) => [...prev, ...response.data.media]);
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.error || 'Failed to upload files');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleCameraCapture = async (files, capturedCaption) => {
    if (capturedCaption && !content.trim()) {
      setContent(capturedCaption);
    }
    if (files && files.length > 0) {
      if (media.length + files.length > 4) {
        toast.error('Maximum 4 attachments allowed');
        return;
      }
      setUploading(true);
      try {
        const filesToUpload = await compressMediaFiles(files, { type: 'post' });
        const formData = new FormData();
        formData.append('subfolder', 'posts');
        filesToUpload.forEach((file) => {
          formData.append('files', file);
        });
        const response = await axios.post('/api/upload/multiple', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        setMedia((prev) => [...prev, ...response.data.media]);
      } catch (error) {
        console.error('Upload error:', error);
        toast.error(error.response?.data?.error || 'Failed to upload files');
      } finally {
        setUploading(false);
      }
    }
  };

  const handleTextFileSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    if (media.length + files.length > 4) {
      toast.error('Maximum 4 attachments allowed');
      return;
    }
    const textFile = files.find((f) => f.type === 'text/plain' || f.name.endsWith('.txt'));
    if (!textFile) {
      toast.error('Please select a valid .txt file');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('files', textFile);
      const response = await axios.post('/api/upload/multiple', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const textItems = (response.data.media || []).filter((m) => m.type === 'text');
      setMedia((prev) => [...prev, ...textItems]);
    } catch (err) {
      console.error('Text upload error:', err);
      toast.error(err.response?.data?.error || 'Failed to upload text file');
    } finally {
      setUploading(false);
      if (textFileInputRef.current) textFileInputRef.current.value = '';
    }
  };

  const handleAddLocation = () => {
    if (!navigator.geolocation) {
      toast.error('Location is not supported by your browser');
      return;
    }
    if (media.length >= 4) {
      toast.error('Maximum 4 attachments allowed');
      return;
    }
    setLocationLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords;
        const url = `https://www.google.com/maps?q=${latitude},${longitude}`;
        setMedia((prev) => [...prev, { url, type: 'location' }]);
        setLocationLoading(false);
      },
      () => {
        toast.error('Could not access your location. Check browser permissions.');
        setLocationLoading(false);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
    );
  };

  const removeMedia = (index) => {
    setMedia((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmed = content.trim();
    if ((!trimmed && media.length === 0) || content.length > 280 || loading || uploading) return;

    setLoading(true);
    try {
      const response = await axios.post(`/api/posts/${postId}/comment`, {
        content: trimmed,
        media,
        parentCommentId: parentCommentId || null,
      });
      setContent('');
      setMedia([]);
      toast.success(parentCommentId ? 'Reply posted!' : 'Comment posted!');
      if (onCommentCreated) onCommentCreated(response.data);
      if (onCancel) onCancel();
    } catch (error) {
      console.error('Create comment error:', error);
      toast.error(error.response?.data?.error || 'Failed to post comment');
    } finally {
      setLoading(false);
    }
  };

  const replyHandle = replyToUser ? (typeof replyToUser === 'string' ? replyToUser : replyToUser.handle) : null;
  const defaultPlaceholder = placeholder || (replyHandle ? `Reply to @${replyHandle}...` : parentCommentId ? 'Post your reply...' : 'Share your thoughts...');
  const charsRemaining = 280 - content.length;

  return (
    <div className={`relative transition-all ${compact ? 'text-xs' : 'text-sm'}`}>
      {showGifPicker && (
        <GifPicker
          onSelect={(item) => {
            if (media.length < 4) setMedia((prev) => [...prev, item]);
            setShowGifPicker(false);
          }}
          onClose={() => setShowGifPicker(false)}
        />
      )}

      <form onSubmit={handleSubmit} className="flex gap-2.5 sm:gap-3">
        {/* User avatar - click to view profile */}
        {user && (
          <Link
            to={user.handle ? `/profile/${user.handle.replace(/^@+/, '')}` : '#'}
            className="flex-shrink-0 pt-0.5 group/avatar transition-transform hover:scale-105 active:scale-95"
            title={user.username ? `View ${user.username}'s profile` : 'View profile'}
          >
            {userProfilePic && !avatarError ? (
              <img
                src={getMediaUrl(userProfilePic)}
                alt={user.username || 'You'}
                onError={() => setAvatarError(true)}
                className={`rounded-full object-cover ring-1 ring-zinc-200 dark:ring-zinc-800 group-hover/avatar:ring-2 group-hover/avatar:ring-sky-500/50 transition-all ${compact ? 'w-7 h-7' : 'w-9 h-9'}`}
              />
            ) : (
              <div
                className={`rounded-full bg-zinc-200 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-300 flex items-center justify-center font-semibold group-hover/avatar:ring-2 group-hover/avatar:ring-sky-500/50 transition-all ${
                  compact ? 'w-7 h-7 text-xs' : 'w-9 h-9 text-sm'
                }`}
              >
                {(user.username || user.handle || 'U').charAt(0).toUpperCase()}
              </div>
            )}
          </Link>
        )}

        <div className="flex-1 min-w-0">
          {/* Replying-to indication */}
          {replyHandle && (
            <div className="flex items-center gap-1.5 text-xs text-zinc-400 dark:text-zinc-500 mb-1">
              <span>Replying to</span>
              <span className="text-sky-600 dark:text-sky-400 font-medium">@{replyHandle}</span>
            </div>
          )}

          {/* Textarea */}
          <div className="relative">
            <textarea
              ref={textareaRef}
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder={defaultPlaceholder}
              rows={compact ? 1 : 2}
              maxLength={280}
              className={`w-full bg-zinc-50/80 dark:bg-zinc-900/60 text-zinc-900 dark:text-zinc-100 placeholder-zinc-400 dark:placeholder-zinc-500 rounded-2xl px-3.5 py-2.5 border border-zinc-200/80 dark:border-zinc-800 focus:outline-none focus:ring-2 focus:ring-sky-500/30 focus:border-sky-500 resize-none transition-all ${
                compact ? 'text-xs min-h-[38px]' : 'text-sm min-h-[46px]'
              }`}
            />
            {/* Mention Suggestions Autocomplete */}
            <MentionSuggestions
              textareaRef={textareaRef}
              content={content}
              setContent={setContent}
            />
          </div>

          {/* Media Attachments Preview */}
          {media.length > 0 && (
            <div className="mt-2 space-y-2">
              <div className={media.filter((m) => m.type === 'image' || m.type === 'video').length <= 1 ? '' : 'grid grid-cols-2 gap-2'}>
                {media.map((item, index) => {
                  if (item.type === 'text') {
                    return (
                      <div
                        key={index}
                        className="inline-flex items-center gap-2 px-3 py-1.5 bg-zinc-100 dark:bg-zinc-800/80 rounded-xl border border-zinc-200 dark:border-zinc-700 text-xs text-zinc-700 dark:text-zinc-200"
                      >
                        <i className="fas fa-file-lines text-zinc-500 text-xs" />
                        <span className="truncate max-w-[150px]">Text attachment</span>
                        <button
                          type="button"
                          onClick={() => removeMedia(index)}
                          className="text-zinc-400 hover:text-rose-500 p-0.5 ml-1 transition-colors"
                        >
                          <i className="fas fa-times text-xs" />
                        </button>
                      </div>
                    );
                  }

                  if (item.type === 'location') {
                    return (
                      <div
                        key={index}
                        className="inline-flex items-center gap-2 px-3 py-1.5 bg-zinc-100 dark:bg-zinc-800/80 rounded-xl border border-zinc-200 dark:border-zinc-700 text-xs text-zinc-700 dark:text-zinc-200"
                      >
                        <i className="fas fa-location-dot text-rose-500 text-xs" />
                        <span className="truncate max-w-[150px]">Location attached</span>
                        <button
                          type="button"
                          onClick={() => removeMedia(index)}
                          className="text-zinc-400 hover:text-rose-500 p-0.5 ml-1 transition-colors"
                        >
                          <i className="fas fa-times text-xs" />
                        </button>
                      </div>
                    );
                  }

                  return (
                    <div key={index} className="relative group rounded-xl overflow-hidden bg-black/5 dark:bg-white/5">
                      {item.type === 'image' ? (
                        <img
                          src={getMediaUrl(item.url)}
                          alt={`Attachment ${index + 1}`}
                          className={`w-full object-cover ${media.filter((m) => m.type === 'image' || m.type === 'video').length === 1 ? 'max-h-60 rounded-xl' : 'h-28 rounded-xl'}`}
                        />
                      ) : (
                        <video
                          src={getMediaUrl(item.url)}
                          className={`w-full object-cover ${media.filter((m) => m.type === 'image' || m.type === 'video').length === 1 ? 'max-h-60 rounded-xl' : 'h-28 rounded-xl'}`}
                          controls
                        />
                      )}
                      <button
                        type="button"
                        onClick={() => removeMedia(index)}
                        className="absolute top-2 right-2 bg-black/70 hover:bg-black/90 text-white rounded-full w-6 h-6 flex items-center justify-center transition-all shadow-md"
                        aria-label="Remove attachment"
                      >
                        <i className="fas fa-times text-[10px]" />
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Action toolbar */}
          <div className="flex items-center justify-between mt-2 pt-1">
            <div className="flex items-center gap-0.5 sm:gap-1 text-zinc-500 dark:text-zinc-400">
              <label
                className={`p-1.5 rounded-full hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors cursor-pointer ${
                  media.length >= 4 || uploading ? 'opacity-40 cursor-not-allowed' : ''
                }`}
                title="Photo / Video"
              >
                <i className="far fa-image text-sm text-sky-500" />
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept="image/*,video/*"
                  onChange={handleFileSelect}
                  className="hidden"
                  disabled={uploading || media.length >= 4}
                />
              </label>

              {/* Camera Photo Capture */}
              <button
                type="button"
                onClick={() => setShowCamera(true)}
                disabled={media.length >= 4 || uploading}
                className="p-1.5 rounded-full hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors disabled:opacity-40"
                title="Take photo"
              >
                <i className="fas fa-camera text-sm text-sky-500" />
              </button>

              <button
                type="button"
                onClick={() => setShowGifPicker((v) => !v)}
                disabled={media.length >= 4}
                className="p-1.5 rounded-full hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors disabled:opacity-40"
                title="GIF"
              >
                <span className="text-[11px] font-bold border border-current px-1 py-0.2 rounded text-sky-500">GIF</span>
              </button>

              <label
                className={`p-1.5 rounded-full hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors cursor-pointer ${
                  media.length >= 4 || uploading ? 'opacity-40 cursor-not-allowed' : ''
                }`}
                title="Text file"
              >
                <i className="far fa-file-alt text-sm text-sky-500" />
                <input
                  ref={textFileInputRef}
                  type="file"
                  accept=".txt,text/plain"
                  onChange={handleTextFileSelect}
                  className="hidden"
                  disabled={uploading || media.length >= 4}
                />
              </label>

              <button
                type="button"
                onClick={handleAddLocation}
                disabled={media.length >= 4 || locationLoading}
                className="p-1.5 rounded-full hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors disabled:opacity-40"
                title="Location"
              >
                {locationLoading ? (
                  <div className="w-3.5 h-3.5 border-2 border-sky-500 border-t-transparent rounded-full animate-spin" />
                ) : (
                  <i className="fas fa-location-dot text-sm text-sky-500" />
                )}
              </button>
            </div>

            <div className="flex items-center gap-2">
              {/* Character countdown indicator */}
              {content.length > 0 && (
                <span
                  className={`text-[11px] font-medium transition-colors ${
                    charsRemaining < 0
                      ? 'text-rose-500 font-bold'
                      : charsRemaining <= 20
                      ? 'text-amber-500'
                      : 'text-zinc-400 dark:text-zinc-500'
                  }`}
                >
                  {charsRemaining}
                </span>
              )}

              {onCancel && (
                <button
                  type="button"
                  onClick={onCancel}
                  className="px-3 py-1.5 text-xs font-medium text-zinc-500 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded-full transition-all"
                >
                  Cancel
                </button>
              )}

              <button
                type="submit"
                disabled={(!content.trim() && media.length === 0) || charsRemaining < 0 || loading || uploading}
                className={`px-4 py-1.5 rounded-full font-medium transition-all shadow-sm flex items-center gap-1.5 active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed ${
                  compact ? 'text-xs' : 'text-xs sm:text-sm'
                } bg-zinc-900 dark:bg-white text-white dark:text-zinc-900 hover:bg-zinc-800 dark:hover:bg-zinc-100`}
              >
                {loading || uploading ? (
                  <>
                    <div className="w-3.5 h-3.5 border-2 border-current border-t-transparent rounded-full animate-spin" />
                    <span>{uploading ? 'Uploading...' : 'Posting...'}</span>
                  </>
                ) : (
                  <>
                    <i className={`fas ${parentCommentId ? 'fa-reply' : 'fa-paper-plane'} text-xs`} />
                    <span>{parentCommentId ? 'Reply' : 'Comment'}</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </form>

      {/* In-app Camera Modal */}
      <CameraModal
        isOpen={showCamera}
        onClose={() => setShowCamera(false)}
        onCapture={handleCameraCapture}
        maxPhotos={Math.max(1, 4 - media.length)}
        initialCaption={content}
      />
    </div>
  );
};

export default CreateComment;
